
import { useState } from 'react';
import { CardDetails, CardVerificationState, CardVerificationResult, VerifiedCardData } from './types';
import { performAiVerification } from './aiVerification';
import { performStripeVerification } from './stripeVerification';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useCardVerification = () => {
  const { toast } = useToast();
  const [state, setState] = useState<CardVerificationState>({
    isVerifying: false,
    isVerified: false,
    verificationScore: null,
    riskLevel: null,
    stripeReady: false,
    stripeData: null,
    cardData: {
      cardNumber: '',
      cardholderName: '',
      expiryDate: '',
      cvv: '',
      saveCard: false,
    },
  });

  const resetVerification = () => {
    setState(prev => ({
      ...prev,
      isVerifying: false,
      isVerified: false,
      verificationScore: null,
      riskLevel: null,
      stripeReady: false,
      stripeData: null,
    }));
  };

  const updateCardData = (data: Partial<CardDetails>) => {
    setState(prev => ({
      ...prev,
      cardData: { ...prev.cardData, ...data },
    }));
  };

  const verifyCard = async (cardData?: CardDetails): Promise<CardVerificationResult> => {
    // Don't proceed if already verifying
    if (state.isVerifying) {
      return {
        success: false,
        message: 'Verification already in progress',
        score: 0,
        riskLevel: 'high',
        stripeReady: false,
      };
    }

    try {
      setState(prev => ({ ...prev, isVerifying: true, verificationError: undefined }));
      
      // Use provided card data or fall back to state
      const verificationCardData = cardData || state.cardData;
      
      // Log card data being used for verification
      console.log('Card data for verification:', {
        cardNumberLength: verificationCardData.cardNumber.length,
        cardholderName: !!verificationCardData.cardholderName,
        expiryDate: verificationCardData.expiryDate,
        cvvProvided: !!verificationCardData.cvv
      });

      // Check if logged in
      const { data: sessionData } = await supabase.auth.getSession();
      const isLoggedIn = !!sessionData.session;

      // First verify with AI
      const aiResult = await performAiVerification(verificationCardData);

      if (!aiResult.success) {
        setState(prev => ({
          ...prev,
          isVerifying: false,
          isVerified: false,
          verificationError: aiResult.message,
          verificationScore: aiResult.score,
          riskLevel: aiResult.riskLevel,
        }));
        return aiResult;
      }

      // AI verification successful, update state
      setState(prev => ({
        ...prev,
        verificationScore: aiResult.score,
        riskLevel: aiResult.riskLevel,
      }));

      // If user is logged in and AI verification succeeded, proceed with Stripe verification
      if (isLoggedIn && aiResult.success) {
        // Process Stripe verification
        const stripeResult = await performStripeVerification(verificationCardData, aiResult);
        
        // Update state with Stripe verification results
        setState(prev => ({
          ...prev,
          isVerifying: false,
          isVerified: stripeResult.success,
          stripeReady: stripeResult.success,
          stripeData: stripeResult.stripeResponse,
          verificationError: stripeResult.success ? undefined : stripeResult.message,
          cardData: {
            ...prev.cardData,
            cardBrand: stripeResult.cardBrand,
            last4: stripeResult.last4,
            stripePaymentMethodId: stripeResult.stripePaymentMethodId,
          }
        }));
        
        return {
          ...stripeResult,
          score: aiResult.score,
          riskLevel: aiResult.riskLevel,
        };
      } else {
        // No Stripe verification needed or possible, just use AI results
        setState(prev => ({
          ...prev,
          isVerifying: false,
          isVerified: aiResult.success,
          stripeReady: aiResult.stripeReady,
        }));
        
        return aiResult;
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      const errorMessage = error.message || 'Card verification failed';
      
      setState(prev => ({
        ...prev,
        isVerifying: false,
        isVerified: false,
        verificationError: errorMessage,
      }));
      
      toast({
        title: "Verification Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      return {
        success: false,
        message: errorMessage,
        score: 0,
        riskLevel: 'high',
        stripeReady: false,
      };
    }
  };

  // Add the missing onVerificationComplete function
  const onVerificationComplete = (success: boolean) => {
    if (!success) {
      resetVerification();
    }
    // When verification is successful, we'll keep the current state
  };

  return {
    ...state,
    updateCardData,
    verifyCard,
    resetVerification,
    onVerificationComplete
  };
};
